import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Checkbox,
  Badge,
  useToast,
  IconButton,
  Box,
  Divider,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiArrowUp, FiArrowDown, FiRotateCcw } from 'react-icons/fi';
import { ALL_COLUMNS } from './tableColumnDefinitions';
import { useAuth } from '../../contexts/AuthContext';

export default function TableCustomizationModal({
  isOpen,
  onClose,
  visibleColumns,
  columnOrder,
  onSave,
  onReset,
  allColumns, // Accept as prop to support both deals and contacts
}) {
  const [localVisibleColumns, setLocalVisibleColumns] = useState(visibleColumns);
  const [localColumnOrder, setLocalColumnOrder] = useState(columnOrder);
  const toast = useToast();
  const { SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  // Use provided columns or fallback to ALL_COLUMNS (for backward compatibility).
  // Admin-only columns (e.g. record scope) never appear in non-admin pickers.
  const columns = (allColumns || ALL_COLUMNS).filter(c => !c.adminOnly || isCRMAdmin);

  const handleToggleColumn = (columnId) => {
    if (localVisibleColumns.includes(columnId)) {
      // Remove from visible and order
      setLocalVisibleColumns(prev => prev.filter(id => id !== columnId));
      setLocalColumnOrder(prev => prev.filter(id => id !== columnId));
    } else {
      // Add to visible and order
      setLocalVisibleColumns(prev => [...prev, columnId]);
      setLocalColumnOrder(prev => [...prev, columnId]);
    }
  };

  const handleMoveUp = (columnId) => {
    const index = localColumnOrder.indexOf(columnId);
    if (index > 0) {
      const newOrder = [...localColumnOrder];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setLocalColumnOrder(newOrder);
    }
  };

  const handleMoveDown = (columnId) => {
    const index = localColumnOrder.indexOf(columnId);
    if (index < localColumnOrder.length - 1) {
      const newOrder = [...localColumnOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setLocalColumnOrder(newOrder);
    }
  };

  const handleSave = () => {
    if (localVisibleColumns.length === 0) {
      toast({
        title: 'Select at least one column',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    onSave({
      visibleColumns: localVisibleColumns,
      columnOrder: localColumnOrder,
    });

    toast({
      title: 'Table customization saved',
      status: 'success',
      duration: 2000,
    });

    onClose();
  };

  const handleReset = async () => {
    try {
      await onReset();
      toast({
        title: 'Reset to defaults',
        status: 'success',
        duration: 2000,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error resetting',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Get ordered columns for display
  const visibleOrderedColumns = localColumnOrder
    .filter(id => localVisibleColumns.includes(id))
    .map(id => columns.find(c => c.id === id))
    .filter(Boolean);

  const hiddenColumns = columns
    .filter(col => !localVisibleColumns.includes(col.id));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Customize Table View</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                Select which columns to display and arrange their order
              </Text>
            </Alert>

            {/* Visible Columns */}
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="semibold" fontSize="sm">
                  Visible Columns
                </Text>
                <Badge colorScheme="blue">{localVisibleColumns.length}</Badge>
              </HStack>

              <VStack align="stretch" spacing={1}>
                {visibleOrderedColumns.map((column, index) => (
                  <HStack
                    key={column.id}
                    p={2}
                    bg="blue.50"
                    borderRadius="md"
                    justify="space-between"
                  >
                    <HStack flex={1}>
                      <Checkbox
                        isChecked={true}
                        onChange={() => handleToggleColumn(column.id)}
                      >
                        <Text fontSize="sm">{column.label}</Text>
                      </Checkbox>
                      {column.isDefault && (
                        <Badge size="xs" colorScheme="green">
                          Default
                        </Badge>
                      )}
                    </HStack>
                    
                    <HStack spacing={1}>
                      <IconButton
                        icon={<FiArrowUp />}
                        size="xs"
                        aria-label="Move up"
                        onClick={() => handleMoveUp(column.id)}
                        isDisabled={index === 0}
                        variant="ghost"
                      />
                      <IconButton
                        icon={<FiArrowDown />}
                        size="xs"
                        aria-label="Move down"
                        onClick={() => handleMoveDown(column.id)}
                        isDisabled={index === visibleOrderedColumns.length - 1}
                        variant="ghost"
                      />
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            </Box>

            <Divider />

            {/* Hidden Columns */}
            {hiddenColumns.length > 0 && (
              <Box>
                <Text fontWeight="semibold" fontSize="sm" mb={2}>
                  Hidden Columns
                </Text>
                <VStack align="stretch" spacing={1}>
                  {hiddenColumns.map((column) => (
                    <HStack key={column.id} p={2} borderRadius="md">
                      <Checkbox
                        isChecked={false}
                        onChange={() => handleToggleColumn(column.id)}
                      >
                        <Text fontSize="sm" color="gray.600">
                          {column.label}
                        </Text>
                      </Checkbox>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleReset}
            leftIcon={<FiRotateCcw />}
          >
            Reset to Defaults
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Save View
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}


